import React, { useEffect, useState } from "react";
import { Pagination } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  approveWithdrawRequest,
  getAdminWithdrawRequests,
  rejectWithdrawRequest,
} from "../../../api/walletTransaction";
import { sendBNB } from "../../../config/walletConfig";

const WithdrawRequests = () => {
  const rowsPerPage = 10;
  const [activePage, setActivePage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRequests = async (page = activePage, status = statusFilter) => {
    setLoading(true);
    try {
      const response = await getAdminWithdrawRequests({
        page,
        limit: rowsPerPage,
        status,
      });
      setRequests(response?.data?.data || []);
      setTotalCount(response?.data?.total || 0);
      setError(null);
    } catch (err) {
      setError("Failed to fetch withdrawal requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [activePage, statusFilter]);

  const formatStatus = (status) => {
    if (status === "pending") return "Pending";
    if (status === "confirm") return "Approved";
    if (status === "cancel") return "Rejected";
    return status || "N/A";
  };

  const handleApprove = async (item) => {
    try {
      if (!item?.walletAddress) {
        Swal.fire({
          icon: "error",
          title: "Missing Wallet Address",
          text: "This request has no destination wallet address.",
        });
        return;
      }

      const txHash = await sendBNB(Number(item.quantity), item.walletAddress);
      if (!txHash) {
        Swal.fire({
          icon: "error",
          title: "Transaction Failed",
          text: "Could not get transaction hash from wallet.",
        });
        return;
      }

      const status = await approveWithdrawRequest(item.id, txHash);
      if (status === 200 || status === 201) {
        Swal.fire({
          icon: "success",
          title: "Withdrawal Approved",
          timer: 1400,
          showConfirmButton: false,
        });
        await loadRequests();
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Approval Failed",
        text: err?.response?.data?.message || "Could not approve request.",
      });
    }
  };

  const handleReject = async (item) => {
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "Reject Request?",
        text: "This request will be marked rejected.",
        showCancelButton: true,
        confirmButtonText: "Reject",
      });

      if (!result.isConfirmed) return;

      const status = await rejectWithdrawRequest(item.id);
      if (status === 200 || status === 201) {
        Swal.fire({
          icon: "success",
          title: "Request Rejected",
          timer: 1200,
          showConfirmButton: false,
        });
        await loadRequests();
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Reject Failed",
        text: err?.response?.data?.message || "Could not reject request.",
      });
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const visiblePages = 5;

    let startPage = Math.max(1, activePage - Math.floor(visiblePages / 2));
    let endPage = startPage + visiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - visiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === activePage}
          onClick={() => setActivePage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-4 pagination-gutter pagination-primary no-bg">
        {items}
      </Pagination>
    );
  };

  if (loading) return <p>Loading withdrawal requests...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header border-0 d-flex justify-content-between align-items-center">
            <h4 className="heading mb-0">Withdraw Req</h4>
            <select
              className="form-select"
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(e) => {
                setActivePage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirm">Approved</option>
              <option value="cancel">Rejected</option>
            </select>
          </div>
          <div className="card-body">
            <div className="table-responsive dataTablehistory">
              <table
                className="table dataTable shadow-hover display"
                style={{ minWidth: "980px" }}
              >
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Wallet Address</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Tx Hash</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length > 0 ? (
                    requests.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>{item?.user?.email || "N/A"}</td>
                        <td>{item?.walletAddress || "N/A"}</td>
                        <td>{Number(item?.quantity || 0).toFixed(2)}</td>
                        <td>{formatStatus(item?.status)}</td>
                        <td>
                          {item?.walletTransactionId
                            ? `${item.walletTransactionId.slice(0, 8)}...${item.walletTransactionId.slice(-6)}`
                            : "-"}
                        </td>
                        <td>
                          {item?.status === "pending" ? (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleApprove(item)}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleReject(item)}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No withdrawal requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="d-flex justify-content-center w-100 mt-3 mb-3">
                {renderPagination()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawRequests;